import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
  MarkerClusterer,
  TrafficLayer,
  TransitLayer,
  BicyclingLayer,
} from "@react-google-maps/api";
import config from "../../config.js";
import { useSettings } from "../../context/SettingsContext";
import { toast } from "react-toastify";
import {
  normalizeDeviceData,
  determineVehicleDirection,
  upsertDirectionForVehicle,
  rehydrateDirections,
  hashString,
  DirectionsCache,
  createCacheKey,
} from "./mapConstants.js";
import {
  getPlaceConfig,
  TransportIcons,
  mapStyles,
  colors,
  containerStyle,
  isValidMapCombination,
  normalizeMapView,
  containerStyleForURL,
} from "./functions.js";
import { getVehicleDirection, updateVehicleDirection } from "./dashboardAPI.js";
import { useStopRedirectEnhanced } from "../../hooks/useUserInfo.js";
import { fitMapToStops, findNearestPointOnRoute } from "./mapUtils.js";
import { getRightToLeftBusIcon } from "../../pages/stops/vehicleIcons.js";
import {
  calculateBearing,
  calculateDistance,
} from "../../utils/calculateDistance.js";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import {
  computeVehicleLabelOffsets,
  computeStopLabelOffsets,
} from "../../utils/mapLabelOffsets.js";
import MapLegend from "./MapLegend.jsx";

const libraries = ["places", "directions"];

function IndependentURLMap({ stops, routes, vehicles }) {
  const { settings, loading } = useSettings();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const { redirectToStopTrack, orgId } = useStopRedirectEnhanced();

  const { getIntermediateStopIcon, getVehicleIcon: getCustomVehicleIcon } =
    useMapIcons(settings, vehicles, stops, routes);

  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [directions, setDirections] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [snappedPositions, setSnappedPositions] = useState({});
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [placeMarkers, setPlaceMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [vehicleStatuses, setVehicleStatuses] = useState({});

  // ─── Label collision offsets ─────────────────────────────────────────────────
  const labelOffsets = useMemo(() => {
    if (!map || !vehicleLocations.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = vehicleLocations.map((v) => {
      const vData = vehicles.find((vv) => vv.device_id === v.deviceId) || {};
      const direction = vehicleDirections[v.deviceId];
      const hasArrow = !!(direction && direction !== "unknown");
      return {
        deviceId: v.deviceId,
        lat: parseFloat(v.latitude),
        lng: parseFloat(v.longitude),
        name: vData.name || "",
        hasArrow,
      };
    });
    return computeVehicleLabelOffsets(projection, map.getZoom(), entries, {
      ANCHOR_SHIFT: 20,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, vehicleLocations, zoom, vehicleDirections]);

  // ─── Stop label collision offsets ────────────────────────────────────────────
  const stopLabelOffsets = useMemo(() => {
    if (!map || !stops.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = stops.map((s) => ({
      id: s.id,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
      name: s.name,
    }));
    return computeStopLabelOffsets(projection, map.getZoom(), entries, {
      BASE_OFFSET: 30,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, stops, zoom]);
  const isMountedRef = useRef(true);
  const eventSourcesRef = useRef({});
  const lastDataReceivedRef = useRef({});
  const [animatedVehicles, setAnimatedVehicles] = useState({});
  const positionHistoryRef = useRef({}); // { [deviceId]: [{lat, lng, timestamp}, ...] }
  const lastKnownHeadings = useRef({}); // { [deviceId]: lastValidHeading }
  const MIN_MOVEMENT_THRESHOLD = 5; // Meters
  const MAX_HISTORY_LENGTH = 5;
  const deviceIdToVehicles_color = useRef({}); // Map deviceId to vehicle color

  const mapDetailsArray = settings.mapDetails || [];
  const mapType = normalizeMapView("roadmap");

  // Populate deviceIdToVehicles_color
  useEffect(() => {
    const colorMap = {};
    vehicles.forEach((vehicle) => {
      colorMap[vehicle.device_id] = vehicle.vehicle_color || "green";
    });
    deviceIdToVehicles_color.current = colorMap;
  }, [vehicles]);

  // Fetch places using Places API
  useEffect(() => {
    if (!isLoaded || !map || !mapDetailsArray.length) {
      setPlaceMarkers([]);
      return;
    }

    const searchTimeout = setTimeout(() => {
      const mapCenter = map.getCenter();
      if (!mapCenter) {
        console.warn("Map center not available yet, skipping places search");
        return;
      }

      const placesService = new window.google.maps.places.PlacesService(map);
      const placeTypes = mapDetailsArray.filter(
        (detail) => detail !== "STREET_NAMES",
      );

      setPlaceMarkers([]);

      if (placeTypes.length === 0) {
        console.log("No place types selected");
        return;
      }

      let centerLat, centerLng;
      try {
        centerLat = mapCenter.lat();
        centerLng = mapCenter.lng();
        if (
          typeof centerLat !== "number" ||
          typeof centerLng !== "number" ||
          isNaN(centerLat) ||
          isNaN(centerLng)
        ) {
          console.warn("Invalid map center coordinates:", {
            centerLat,
            centerLng,
          });
          return;
        }
      } catch (error) {
        console.error("Error getting map center:", error);
        return;
      }

      const allPlacePromises = placeTypes.map((placeType) => {
        return new Promise((resolve) => {
          const placeConfig = getPlaceConfig(placeType);
          if (!placeConfig || !placeConfig.type) {
            console.warn(`No config found for place type: ${placeType}`);
            resolve([]);
            return;
          }

          const request = {
            location: new window.google.maps.LatLng(centerLat, centerLng),
            radius: 20000,
            type: placeConfig.type,
          };

          placesService.nearbySearch(request, (results, status) => {
            if (status === "OK" && results) {
              const markers = results.map((place) => ({
                id: place.place_id,
                name: place.name,
                position: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                },
                type: placeType,
              }));
              resolve(markers);
            } else if (status === "ZERO_RESULTS") {
              resolve([]);
            } else {
              console.error(`Places search failed for ${placeType}:`, status);
              resolve([]);
            }
          });
        });
      });

      Promise.all(allPlacePromises).then((allMarkers) => {
        setPlaceMarkers(allMarkers.flat());
      });
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [isLoaded, map, mapDetailsArray]);

  const getMapOptions = useCallback(() => {
    const showStreetNames = mapDetailsArray.includes("STREET_NAMES");
    return {
      styles: [
        ...mapStyles,
        {
          featureType: "road",
          elementType: "labels.text",
          stylers: [{ visibility: showStreetNames ? "on" : "off" }],
        },
        {
          featureType: "water",
          elementType: "labels.text",
          stylers: [{ visibility: "on" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text",
          stylers: [{ visibility: "on" }],
        },
      ],
      mapTypeId: mapType,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: true,
      streetViewControl: true,
      rotateControl: true,
      fullscreenControl: true,
    };
  }, [mapType, mapDetailsArray]);

  const handleStopClick = useCallback(
    (stop) => {
      try {
        if (!stop || !stop.id) {
          toast.error("Invalid stop data");
          return;
        }
        if (!orgId) {
          toast.error(
            "Organization ID not found. Please check your login status.",
          );
          return;
        }
        console.log(
          `Attempting to redirect to stop ${stop.id} with org ${orgId}`,
        );
        const success = redirectToStopTrack(stop.id);
        if (!success) {
          toast.error("Failed to redirect to stop tracking page");
        } else {
          toast.info(
            `Redirecting to track stop: ${stop.name || `Stop ${stop.id}`}`,
          );
        }
      } catch (error) {
        console.error("Error handling stop click:", error);
        toast.error("An error occurred while redirecting");
      }
    },
    [redirectToStopTrack, orgId],
  );

  useEffect(() => {
    const deviceIds = vehicles.map((v) => v.device_id);
    setVehicleIds(deviceIds);
  }, [vehicles]);

  const fetchInitialLocations = useCallback(async () => {
    if (!vehicleIds.length) return;
    try {
      const promises = vehicleIds.map(async (deviceId) => {
        const response = await fetch(
          `${config.baseURL}/location/latest/${deviceId}`,
        );
        const data = await response.json();
        if (data && data.latitude != null && data.longitude != null) {
          const normalizedData = normalizeDeviceData(data);
          const vehicleData = vehicles.find((v) => v.device_id === deviceId);
          const route = routes.find((r) => r.id === vehicleData?.routeId);
          const direction = directions.find(
            (d) => d.routeColor === route?.routeColor,
          );

          let snappedPosition = null;
          if (direction) {
            const position = {
              lat: parseFloat(data.latitude),
              lng: parseFloat(data.longitude),
            };
            snappedPosition = findNearestPointOnRoute(position, direction);
          }

          positionHistoryRef.current[deviceId] = [
            {
              lat: parseFloat(normalizedData.latitude),
              lng: parseFloat(normalizedData.longitude),
              timestamp: Date.now(),
            },
          ];
          const storedData = JSON.parse(localStorage.getItem(deviceId) || "{}");
          const initialHeading = storedData.lastHeading || 0;
          lastKnownHeadings.current[deviceId] = initialHeading;

          return {
            ...normalizedData,
            latitude: snappedPosition
              ? snappedPosition.lat
              : normalizedData.latitude,
            longitude: snappedPosition
              ? snappedPosition.lng
              : normalizedData.longitude,
            heading: initialHeading,
          };
        } else {
          console.warn(`No valid location data for device ${deviceId}`, data);
          return null;
        }
      });
      const locations = await Promise.all(promises);
      const validLocations = locations.filter((loc) => loc !== null);

      const newSnappedPositions = {};
      validLocations.forEach((loc) => {
        const vehicleData = vehicles.find((v) => v.device_id === loc.deviceId);
        const route = routes.find((r) => r.id === vehicleData?.routeId);
        const direction = directions.find(
          (d) => d.routeColor === route?.routeColor,
        );
        if (direction) {
          const position = {
            lat: parseFloat(loc.latitude),
            lng: parseFloat(loc.longitude),
          };
          const snapped = findNearestPointOnRoute(position, direction);
          if (snapped) {
            newSnappedPositions[loc.deviceId] = snapped;
          }
        }
      });
      setSnappedPositions(newSnappedPositions);
      setVehicleLocations(validLocations);
    } catch (error) {
      console.error("Error fetching initial vehicle locations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [vehicleIds, vehicles, routes, directions]);

  useEffect(() => {
    isMountedRef.current = true;
    if (vehicleIds.length) {
      fetchInitialLocations();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [vehicleIds, fetchInitialLocations]);

  useEffect(() => {
    if (isLoading || !vehicleIds.length) return;

    const fetchDirectionsFromRedis = async () => {
      try {
        const directions = {};
        await Promise.all(
          vehicleIds.map(async (deviceId) => {
            const dir = await getVehicleDirection(deviceId);
            // console.log(
            //   `Fetching direction for ${deviceId} from Redis: ${dir}`,
            // );
            if (dir) {
              directions[deviceId] = dir;
            } else {
              console.warn(
                `No direction found in Redis for ${deviceId}, will remain unknown`,
              );
            }
          }),
        );
        setVehicleDirections((prev) => ({ ...prev, ...directions }));
      } catch (error) {
        console.error("Error fetching directions from Redis:", error);
      }
    };

    // Initial call
    fetchDirectionsFromRedis();

    // Set interval for every 30 seconds (30,000 ms)
    const interval = setInterval(fetchDirectionsFromRedis, 30000);

    // Cleanup on unmount or dependency change
    return () => clearInterval(interval);
  }, [vehicleIds, isLoading]);

  useEffect(() => {
    if (isLoading || !vehicleIds.length) return;

    const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const eventSource = new EventSource(sseUrl);
    eventSourcesRef.current = { main: eventSource };

    eventSource.onopen = () => {};

    eventSource.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type && data.type === "connected") {
          console.log("Received connection confirmation:", data);
          return;
        }

        const normalizedData = normalizeDeviceData(data);
        if (
          normalizedData.deviceId &&
          normalizedData.latitude &&
          normalizedData.longitude
        ) {
          lastDataReceivedRef.current[normalizedData.deviceId] = Date.now();
          setVehicleStatuses((prev) => ({
            ...prev,
            [normalizedData.deviceId]: "ACTIVE",
          }));

          const vehicleData = vehicles.find(
            (v) => v.device_id === normalizedData.deviceId,
          );
          const route = routes.find((r) => r.id === vehicleData?.routeId);
          const direction = directions.find(
            (d) => d.routeColor === route?.routeColor,
          );

          let snappedPosition = null;
          if (direction) {
            const position = {
              lat: parseFloat(normalizedData.latitude),
              lng: parseFloat(normalizedData.longitude),
            };
            snappedPosition = findNearestPointOnRoute(position, direction);
          }

          const newPosition = {
            lat: snappedPosition
              ? snappedPosition.lat
              : parseFloat(normalizedData.latitude),
            lng: snappedPosition
              ? snappedPosition.lng
              : parseFloat(normalizedData.longitude),
          };

          if (!positionHistoryRef.current[normalizedData.deviceId]) {
            positionHistoryRef.current[normalizedData.deviceId] = [];
          }
          positionHistoryRef.current[normalizedData.deviceId].push({
            lat: newPosition.lat,
            lng: newPosition.lng,
            timestamp: Date.now(),
          });
          if (
            positionHistoryRef.current[normalizedData.deviceId].length >
            MAX_HISTORY_LENGTH
          ) {
            positionHistoryRef.current[normalizedData.deviceId] =
              positionHistoryRef.current[normalizedData.deviceId].slice(
                -MAX_HISTORY_LENGTH,
              );
          }

          let heading = lastKnownHeadings.current[normalizedData.deviceId] || 0;
          const history = positionHistoryRef.current[normalizedData.deviceId];
          if (history.length >= 2) {
            const prevPos = history[history.length - 2];
            const currPos = history[history.length - 1];
            const distMoved = calculateDistance(prevPos, currPos);
            if (distMoved > MIN_MOVEMENT_THRESHOLD) {
              heading = calculateBearing(
                { latitude: prevPos.lat, longitude: prevPos.lng },
                { latitude: currPos.lat, longitude: currPos.lng },
              );
              lastKnownHeadings.current[normalizedData.deviceId] = heading;
            }
          }

          const persisted = JSON.parse(
            localStorage.getItem(normalizedData.deviceId) || "{}",
          );
          persisted.lastHeading = heading;
          localStorage.setItem(
            normalizedData.deviceId,
            JSON.stringify(persisted),
          );

          setVehicleLocations((prev) => {
            const existingIndex = prev.findIndex(
              (item) => item.deviceId === normalizedData.deviceId,
            );
            const updatedData = {
              ...normalizedData,
              latitude: newPosition.lat,
              longitude: newPosition.lng,
              heading,
            };
            if (existingIndex >= 0) {
              const newLocations = [...prev];
              newLocations[existingIndex] = updatedData;
              return newLocations;
            }
            return [...prev, updatedData];
          });

          setSnappedPositions((prev) => ({
            ...prev,
            [normalizedData.deviceId]: snappedPosition,
          }));

          await upsertDirectionForVehicle({
            deviceId: normalizedData.deviceId,
            currentPosition: {
              lat: parseFloat(normalizedData.latitude),
              lng: parseFloat(normalizedData.longitude),
            },
            stops,
            routes,
            vehicles,
            setVehicleDirections: (updater) => {
              setVehicleDirections((prev) => {
                const nextState =
                  typeof updater === "function" ? updater(prev) : updater;
                const newDir = nextState[normalizedData.deviceId];
                const oldDir = prev[normalizedData.deviceId];
                if (newDir === "unknown" && oldDir && oldDir !== "unknown") {
                  return prev;
                }
                return nextState;
              });
            },
            onDirectionChange: async (id, dir) => {
              await updateVehicleDirection(id, dir);
            },
          });
        }
      } catch (error) {
        console.error("Error processing SSE data:", error);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE connection error:", error);
      if (eventSourcesRef.current.main) {
        eventSourcesRef.current.main.close();
        delete eventSourcesRef.current.main;
      }
    };

    return () => {
      if (eventSourcesRef.current.main) {
        console.log("Closing SSE connection");
        eventSourcesRef.current.main.close();
        eventSourcesRef.current = {};
      }
    };
  }, [vehicleIds, isLoading, stops, routes, vehicles]);

  const onLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      setTimeout(() => {
        fitMapToStops(mapInstance, stops, vehicleLocations, snappedPositions);
      }, 100);
    },
    [stops, vehicleLocations, snappedPositions],
  );

  useEffect(() => {
    if (map && !isLoading) {
      fitMapToStops(map, stops, vehicleLocations, snappedPositions);
    }
  }, [map, stops, vehicleLocations, snappedPositions, isLoading]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (isLoaded && routes.length > 0 && map) {
      const directionsService = new window.google.maps.DirectionsService();
      const fetchDirections = async () => {
        const directionsPromises = routes.map((route) => {
          return new Promise((resolve, reject) => {
            const cacheKey = createCacheKey(route, stops);
            const cachedDirections = DirectionsCache.get(cacheKey);
            if (cachedDirections) {
              resolve({
                ...cachedDirections,
                routeColor: route.routeColor || cachedDirections.routeColor,
              });
              return;
            }

            console.log(
              `[Directions Cache] No valid cache for route ${route.id}, fetching from Maps API`,
            );

            const waypoints = route.routeCoordinates
              .slice(1, -1)
              .map((coord) => ({
                location: new window.google.maps.LatLng(
                  parseFloat(coord.latitude),
                  parseFloat(coord.longitude),
                ),
                stopover: coord.isStop || false,
              }));

            const origin = new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude),
            );

            const destination = new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .latitude,
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .longitude,
              ),
            );

            directionsService.route(
              {
                origin,
                destination,
                waypoints,
                optimizeWaypoints: false,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  console.log(
                    `[Directions Cache] Successfully fetched from Maps API for route ${route.id}`,
                  );
                  DirectionsCache.set(cacheKey, result, route.routeColor);
                  resolve({ ...result, routeColor: route.routeColor });
                } else {
                  console.error(
                    `[Directions Cache] Maps API error for route ${route.id}: ${status}`,
                  );
                  reject(new Error(`Directions API error: ${status}`));
                }
              },
            );
          });
        });

        try {
          const results = await Promise.all(directionsPromises);
          const validResults = results.filter(
            (result) =>
              result &&
              result.routes &&
              result.routes.length > 0 &&
              result.routes[0].legs &&
              result.routes[0].legs.length > 0,
          );
          setDirections(validResults);
          console.log(
            `[Directions Cache] Loaded ${validResults.length} valid route directions`,
          );
        } catch (error) {
          console.error("Error fetching directions:", error);
          setDirections([]);
        }
      };

      fetchDirections();
    }
  }, [isLoaded, routes, map, stops]);

  useEffect(() => {
    const statusCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeoutThreshold = 5 * 60 * 1000;
      setVehicleStatuses((prev) => {
        const newStatuses = { ...prev };
        vehicleIds.forEach((deviceId) => {
          const lastReceived = lastDataReceivedRef.current[deviceId];
          if (!lastReceived || now - lastReceived > timeoutThreshold) {
            newStatuses[deviceId] = "DEAD";
          }
        });
        return newStatuses;
      });
    }, 180000);
    return () => clearInterval(statusCheckInterval);
  }, [vehicleIds]);

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const getDirectionArrow = (deviceId) => {
    const direction = vehicleDirections[deviceId];
    if (!direction || direction === "unknown") return "";
    let dir = true
      ? direction === "source_to_dest"
        ? " ➡️"
        : " ⬅️"
      : direction === "source_to_dest"
        ? " ⬅️"
        : " ➡️";

    return dir;
  };

  const getVehicleIcon = (deviceId, heading) => {
    const color = deviceIdToVehicles_color.current[deviceId] || "green";
    const direction = vehicleDirections[deviceId];
    if (!direction || direction === "unknown") return "";
    const adjustedHeading =
      direction !== "source_to_dest" ? (heading + 180) % 360 : heading;
    let head = false ? adjustedHeading + 90 : heading + 90;
    const rotatedSVG = getRightToLeftBusIcon(color, head).trim();
    const encodedSVG = encodeURIComponent(rotatedSVG);

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
      scaledSize: new window.google.maps.Size(45, 45),
      anchor: new window.google.maps.Point(22.5, 23),
    };
  };

  const getColor = (status, isOffRoute) => {
    if (true) {
      return isOffRoute
        ? "#cf13b9ff"
        : status === "DEAD"
          ? "#F9E719"
          : status === "INACTIVE"
            ? "#F9E719"
            : "#008C23";
    }

    // not demo
    return status === "DEAD"
      ? "#EE3B2B"
      : status === "INACTIVE"
        ? "#F9E719"
        : "#008C23";
  };

  const vehicleLegendIconUrl = useMemo(() => {
    const svg = getRightToLeftBusIcon("green", 180).trim();
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }, []);

  const routeLegendColors = useMemo(
    () => directions.map((direction) => direction.routeColor || "#000000"),
    [directions],
  );

  const placeLegendItems = useMemo(
    () =>
      [...new Set(placeMarkers.map((place) => place.type))].map((type) => {
        const cfg = getPlaceConfig(type);
        return { type, icon: cfg?.icon };
      }),
    [placeMarkers],
  );

  const stopLegendItems = useMemo(() => {
    if (!isLoaded || !window.google?.maps) {
      return [{ label: "Stop", color: "#d32f2f" }];
    }
    const stopIcon = getIntermediateStopIcon();
    return [{ label: "Stop", iconUrl: stopIcon?.url }];
  }, [getIntermediateStopIcon, isLoaded]);

  if (loadError) {
    console.error("Google Maps load error:", loadError);
    return <div>Error loading maps</div>;
  }
  if (!isLoaded || loading) {
    return (
      <div style={{ padding: "40px" }}>
        <div height="30vh" width="100%" />
      </div>
    );
  }

  const fullContainerStyle = {
    width: "100%",
    height: "100vh",
    minHeight: "600px",
  };

  return (
    <>
      <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
        <GoogleMap
          mapContainerStyle={fullContainerStyle}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={getMapOptions()}
          onZoomChanged={() => {
            if (map) setZoom(map.getZoom());
          }}
        >
          {placeMarkers.map((place) => {
            const placeConfig = getPlaceConfig(place.type);
            if (!placeConfig || !placeConfig.icon) return null;
            return (
              <Marker
                key={place.id}
                position={place.position}
                title={place.name}
                icon={{
                  url: placeConfig.icon,
                  scaledSize: new window.google.maps.Size(32, 32),
                }}
              />
            );
          })}

          <MarkerClusterer
            options={(() => {
              const stopIcon = getIntermediateStopIcon();
              const clusterStyle = stopIcon
                ? {
                    url: stopIcon.url,
                    width: 40,
                    height: 40,
                    textColor: "#000000",
                    textSize: 11,
                    fontWeight: "bold",
                    anchorIcon: [40, 20],
                    anchorText: [-2, 0],
                  }
                : {
                    url:
                      "data:image/svg+xml;charset=UTF-8," +
                      encodeURIComponent(
                        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                          <circle cx="20" cy="20" r="18" fill="#d32f2f" stroke="#fff" stroke-width="2"/>
                        </svg>`,
                      ),
                    width: 40,
                    height: 40,
                    textColor: "#ffffff",
                    textSize: 11,
                    fontWeight: "bold",
                    anchorIcon: [20, 20],
                    anchorText: [0, 0],
                  };
              return {
                styles: [clusterStyle, clusterStyle, clusterStyle],
                gridSize: 20,
                minimumClusterSize: 2,
                zoomOnClick: true,
              };
            })()}
          >
            {(clusterer) =>
              stops.map((stop) => {
                const lat = parseFloat(stop.latitude);
                const lng = parseFloat(stop.longitude);
                if (isNaN(lat) || isNaN(lng)) {
                  console.warn(
                    `Invalid coordinates for stop ${stop.id}:`,
                    stop,
                  );
                  return null;
                }
                return (
                  <React.Fragment key={stop.id}>
                    {/* Only show name label when not clustered (zoom >= 14) */}
                    {(zoom === null || zoom >= 14) &&
                      (() => {
                        const offsetPx = stopLabelOffsets[stop.id]?.topPx ?? 52;
                        const connectorH = offsetPx - 26;
                        return (
                          <OverlayView
                            position={{ lat, lng }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                          >
                            <div
                              style={{
                                position: "relative",
                                left: "0px",
                                top: "0px",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  bottom: `${offsetPx}px`,
                                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                                  color: "#333",
                                  padding: "6px 10px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  boxShadow: "0 3px 8px rgba(0,0,0,0.3)",
                                  whiteSpace: "nowrap",
                                  zIndex: 1001,
                                  border: "0.5px solid #4285F4",
                                  maxWidth: "200px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  pointerEvents: "none",
                                }}
                                title={`Click to track ${stop.name || `Stop ${stop.id}`}`}
                              >
                                {stop.name || `Stop ${stop.id}`}
                              </div>
                              {connectorH > 0 && (
                                <svg
                                  style={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    bottom: "26px",
                                    pointerEvents: "none",
                                    overflow: "visible",
                                    zIndex: 1000,
                                  }}
                                  width="2"
                                  height={connectorH}
                                >
                                  <line
                                    x1="1"
                                    y1="0"
                                    x2="1"
                                    y2={connectorH}
                                    stroke="rgba(0,0,0,0.45)"
                                    strokeWidth="1.5"
                                    strokeDasharray="3 2"
                                  />
                                </svg>
                              )}
                            </div>
                          </OverlayView>
                        );
                      })()}
                    <Marker
                      position={{ lat, lng }}
                      title={`${stop.name || `Stop ${stop.id}`} - Click to track`}
                      onClick={() => handleStopClick(stop)}
                      options={{ cursor: "pointer" }}
                      icon={getIntermediateStopIcon() || undefined}
                      clusterer={clusterer}
                    />
                  </React.Fragment>
                );
              })
            }
          </MarkerClusterer>

          {vehicleLocations.map((vehicle) => {
            const vehicleData =
              vehicles.find((v) => v.device_id === vehicle.deviceId) || {};
            const status = false
              ? vehicleData.status
              : vehicleStatuses[vehicle.deviceId] || vehicleData.status;
            // const vehicleIcon = getVehicleIcon(
            //   vehicle.deviceId,
            //   vehicle.heading || 0,
            // );
            const direction = vehicleDirections[vehicle.deviceId];
            const vehicleIcon = getCustomVehicleIcon(
              vehicle.deviceId,
              vehicle.heading || 0,
              direction,
            );
            const vehicleName = vehicleData.name;
            const isOffRoute = !snappedPositions[vehicle.deviceId];
            const currentSpeed = parseFloat(
              vehicle.speed || vehicle.Spped || 0,
            );
            const OVERSPEED_THRESHOLD = settings?.maxSpeed || 30;
            const isOverSpeed = currentSpeed > OVERSPEED_THRESHOLD;
            return (
              vehicle &&
              vehicle.latitude &&
              vehicle.longitude && (
                <React.Fragment key={vehicle.deviceId}>
                  <OverlayView
                    position={{
                      lat: parseFloat(vehicle.latitude),
                      lng: parseFloat(vehicle.longitude),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        position: "relative",
                        top: "20px",
                        left: "0px",
                        width: "2px",
                        height: "2px",
                      }}
                    >
                      {(zoom === null || zoom >= 14) &&
                        (() => {
                          const topPx =
                            labelOffsets[vehicle.deviceId]?.topPx ?? 10;
                          const connectorH = topPx;
                          return (
                            <>
                              {connectorH > 4 && (
                                <svg
                                  style={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    top: "0px",
                                    pointerEvents: "none",
                                    overflow: "visible",
                                    zIndex: 999,
                                  }}
                                  width="2"
                                  height={connectorH}
                                >
                                  <line
                                    x1="1"
                                    y1="0"
                                    x2="1"
                                    y2={connectorH}
                                    stroke="black"
                                    strokeWidth="1.8"
                                    strokeDasharray="3 2"
                                  />
                                </svg>
                              )}
                              <div
                                style={{
                                  position: "absolute",
                                  top:
                                    labelOffsets[vehicle.deviceId]?.top ??
                                    "10px",
                                  left:
                                    labelOffsets[vehicle.deviceId]?.left ??
                                    "50%",
                                  transform:
                                    labelOffsets[vehicle.deviceId]?.transform ??
                                    "translateX(-50%)",
                                  color:
                                    status === "INACTIVE" || status === "DEAD"
                                      ? "#000"
                                      : "#fff",
                                  fontSize: "8px", // Reduced from 10px
                                  fontWeight: 600,
                                  fontFamily: "Arial, sans-serif",
                                  lineHeight: "12px", // Adjusted
                                  padding: "6px", // Adjusted
                                  textAlign: "left",
                                  whiteSpace: "nowrap",
                                  backgroundColor: getColor(status, isOffRoute),
                                  borderRadius: "10px", // Adjusted
                                  zIndex: 1000,
                                  border: "1px solid black",
                                }}
                              >
                                {vehicleName}
                                <span style={{ fontWeight: "bold" }}>
                                  {getDirectionArrow(vehicle.deviceId)}
                                </span>
                                {true && isOffRoute && (
                                  <span> (Off-Route)</span>
                                )}
                              </div>
                            </>
                          );
                        })()}
                    </div>
                  </OverlayView>
                  <Marker
                    position={{
                      lat: parseFloat(vehicle.latitude),
                      lng: parseFloat(vehicle.longitude),
                    }}
                    icon={vehicleIcon}
                  />
                  {isOverSpeed && (
                    <OverlayView
                      position={{
                        lat: parseFloat(vehicle.latitude),
                        lng: parseFloat(vehicle.longitude),
                      }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        style={{
                          position: "relative",
                          top: "-50px",
                          left: "25px",
                        }}
                      >
                        <div
                          title={`OVERSPEED: ${currentSpeed} km/h`}
                          style={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: "#FF0000",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid #FFFFFF",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                            animation: "pulse 1.5s infinite",
                            zIndex: 1001,
                          }}
                        >
                          <span
                            style={{
                              color: "#FFFFFF",
                              fontSize: "20px",
                              fontWeight: "bold",
                            }}
                          >
                            ⚠
                          </span>
                        </div>
                      </div>
                    </OverlayView>
                  )}
                </React.Fragment>
              )
            );
          })}

          {directions.map((direction, index) => (
            <DirectionsRenderer
              key={index}
              directions={direction}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: direction.routeColor || "#000000",
                  strokeOpacity: 0.8,
                  strokeWeight: 4,
                },
              }}
            />
          ))}
          <MapLegend
            showVehicle
            vehicleIconUrl={vehicleLegendIconUrl}
            statusItems={[
              { label: "Active", color: "#008C23", borderColor: "#008C23" },
              { label: "Inactive", color: "#F9E719", borderColor: "#aaa" },
            ]}
            routeColors={routeLegendColors}
            stopItems={stopLegendItems}
            placeItems={placeLegendItems}
            showDirection
          />
        </GoogleMap>
      </div>
    </>
  );
}

export default React.memo(IndependentURLMap);
